import React, { FC } from "react";
import { useLocation, useParams } from "react-router";
import { useQuery } from "react-query";
import * as queryString from "querystring";
import SchoolDefaultLogo from "../../assets/img/school-default-logo.png";
import UserDefaultLogo from "../../assets/svg/user-avatar.svg";
import useStudentsQueries from "../student/hooks/useManagedStudentsQueries";
import { UserRole } from "../user/enums/UserRole";
import { useAuth } from "../auth/hooks/useAuth";
import { MessageWidgetPage } from "./pages/MessageWidgetPage";
import useUserQueries from "../user/hooks/useUserQueries";

const MessagesPageWrapper: FC<any> = ({
  studentId,
  schoolUserId,
  serviceProviderUserId,
}) => {
  const school = {} as { id?: string; image?: string };
  const serviceProvider = {} as { id?: string; image?: string };
  const student = {} as { id?: string; image?: string };
  const { recipientId, userRole } = useParams<{
    recipientId?: string;
    userRole?: string;
  }>();
  const { fetchStudentData } = useStudentsQueries();
  const { fetchUserProfile } = useUserQueries();
  const location = useLocation();
  const queryParams = queryString.parse(location.search.split("?").join(""));
  const recipientIdByQueryParams: any = queryParams.recipientId;
  const userRoleByQueryParams: any =
    queryParams.userRole && +queryParams.userRole;

  const {
    user,
    profile: { data: { data: profile = null } = {} },
  } = useAuth();

  const isAgent = user.role === UserRole.Agent;
  const isAdmin = [UserRole.Owner, UserRole.Admin].includes(user.role);

  //fetch student data to show its image when the messages page is opened on behalf of the student
  const {
    data: { data: studentData = {} } = {},
    isLoading: isLoadingStudent,
    isFetching: isFetchingStudent,
  } = useQuery<any>(
    `${"studentData" + studentId}`,
    () => fetchStudentData(studentId),
    { enabled: isAgent || (isAdmin && studentId) },
  );

  //fetch school data to show its image when the messages page is opened on behalf of the school
  const {
    data: { data: { school: schoolData = {} } = {} } = {},
    isLoading: isLoadingSchool,
    isFetching: isFetchingSchool,
  } = useQuery<any>(
    `${"schoolData" + schoolUserId}`,
    () => fetchUserProfile(schoolUserId),
    { enabled: isAdmin && schoolUserId },
  );

  //fetch service provider data to show its image when the messages page is opened on behalf of the service provider
  const {
    data: { data: { serviceProvider: serviceProviderData = {} } = {} } = {},
    isLoading: isLoadingServiceProvider,
    isFetching: isFetchingServiceProvider,
  } = useQuery<any>(
    `${"serviceProviderData" + serviceProviderUserId}`,
    () => fetchUserProfile(serviceProviderUserId),
    { enabled: isAdmin && serviceProviderUserId },
  );

  switch (user.role) {
    case UserRole.School: {
      school.id = profile?.school.id;
      school.image = profile.school.image?.preview || SchoolDefaultLogo;
      break;
    }
    case UserRole.ServiceProvider: {
      serviceProvider.id = profile?.serviceProvider.id;
      serviceProvider.image =
        profile.serviceProvider.image?.preview || SchoolDefaultLogo;
      break;
    }
    case UserRole.Student: {
      student.id = user.id;
      student.image = user?.photo?.preview || UserDefaultLogo;
      break;
    }
    case UserRole.Agent:
    case UserRole.Parent: {
      student.id = studentId;
      student.image = studentData.photo?.preview || UserDefaultLogo;
      break;
    }
    case UserRole.Owner:
    case UserRole.Admin: {
      if (studentId) {
        student.id = studentId;
        student.image = studentData.photo?.preview || UserDefaultLogo;
      }
      if (schoolUserId) {
        school.id = schoolData.id;
        school.image = schoolData.image?.preview || UserDefaultLogo;
      }
      if (serviceProviderUserId) {
        serviceProvider.id = serviceProviderData.id;
        serviceProvider.image =
          serviceProviderData.image?.preview || UserDefaultLogo;
      }
      break;
    }
  }

  if (
    isLoadingStudent ||
    isFetchingStudent ||
    isLoadingSchool ||
    isFetchingSchool ||
    isLoadingServiceProvider ||
    isFetchingServiceProvider
  ) {
    return <></>;
  }

  return (
    <MessageWidgetPage
      school={school}
      serviceProvider={serviceProvider}
      student={student}
      recipientId={recipientId || recipientIdByQueryParams}
      userRole={userRole ? (+userRole as UserRole) : userRoleByQueryParams}
    />
  );
};

export default MessagesPageWrapper;
